import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from "next";
import Head from "next/head";

import parlors from "@/constants/parlors_detail.json";
import { skipDevBuildInGetStaticPaths } from "@/utils/skipDevBuildInGetStaticPaths";
import { buildDirectionsPageProps } from "@/utils/buildDirectionsPageProps";
import { DirectionsDetailContainer } from "@/containers/DirectionsDetailContainer";

export const getStaticPaths: GetStaticPaths = async () => {
  const skipBuildResult =
    skipDevBuildInGetStaticPaths() as GetStaticPathsResult;

  if (skipBuildResult) {
    return skipBuildResult;
  }

  const paths = parlors.map((parlor) => ({
    params: {
      id: parlor.id.toString(),
    },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const parlorId = parseInt(params?.id as string);
  const {
    companyName,
    sidoCode,
    sigunguCode,
    costMubinso,
    costFamily,
    costGeneral,
    sidoName,
    sigunguName,
    address,
    telephone,
    latitude,
    longitude,
    hasCoordinates,
    traffpublic,
    traffowner,
    amenities,
    parkcnt,
    nearbyParlors,
    title,
    description,
    keywords,
    mainImage,
    jsonLd,
  } = buildDirectionsPageProps(parlorId);

  return {
    props: {
      parlorId,
      companyName,
      sidoCode,
      sigunguCode,
      costMubinso,
      costFamily,
      costGeneral,
      sidoName,
      sigunguName,
      address,
      telephone,
      latitude: latitude ?? null,
      longitude: longitude ?? null,
      hasCoordinates,
      traffpublic,
      traffowner,
      amenities,
      parkcnt,
      nearbyParlors,
      pageDescription: description,
      pageKeywords: keywords,
      pagePublishedTime: "2026-05-22T00:00:00+09:00",
      pageOgTitle: title,
      pageOgDescription: description,
      pageOgImage:
        mainImage || `${process.env.NEXT_PUBLIC_CDN_URL}/ogimage.png`,
      pageJsonLd: JSON.stringify(jsonLd),
      pageCanonical: `https://parlor.goifuneral.co.kr/directions/${parlorId}/`,
    },
  };
};

interface DirectionsPageProps {
  parlorId: number;
  companyName: string;
  sidoCode: string;
  sigunguCode: string;
  costMubinso: number | null;
  costFamily: number | null;
  costGeneral: number | null;
  sidoName: string;
  sigunguName: string;
  address: string;
  telephone: string;
  latitude: number | null;
  longitude: number | null;
  hasCoordinates: boolean;
  traffpublic: string;
  traffowner: string;
  amenities: Record<string, boolean | number>;
  parkcnt: number;
  nearbyParlors: Array<{ id: number; companyname: string; sido: string }>;
  pageJsonLd?: string;
}

const DirectionsPage = ({
  parlorId,
  companyName,
  sidoCode,
  sigunguCode,
  costMubinso,
  costFamily,
  costGeneral,
  sidoName,
  sigunguName,
  address,
  telephone,
  latitude,
  longitude,
  hasCoordinates,
  traffpublic,
  traffowner,
  amenities,
  parkcnt,
  nearbyParlors,
  pageJsonLd,
}: DirectionsPageProps) => {
  return (
    <>
      {pageJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: pageJsonLd }}
          />
        </Head>
      )}
      <DirectionsDetailContainer
        parlorId={parlorId}
        companyName={companyName}
        sidoCode={sidoCode}
        sigunguCode={sigunguCode}
        costMubinso={costMubinso}
        costFamily={costFamily}
        costGeneral={costGeneral}
        sidoName={sidoName}
        sigunguName={sigunguName}
        address={address}
        telephone={telephone}
        latitude={latitude ?? undefined}
        longitude={longitude ?? undefined}
        hasCoordinates={hasCoordinates}
        traffpublic={traffpublic}
        traffowner={traffowner}
        amenities={amenities}
        parkcnt={parkcnt}
        nearbyParlors={nearbyParlors}
      />
    </>
  );
};

export default DirectionsPage;
